/* eslint-disable @reatom/unit-naming-rule */
import { atom } from '@reatom/framework'
import { match } from 'ts-pattern'

import { Result } from '@blade/std-lib'
import { delugeIdAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network'
import { pairRegistry } from '@root/entities/pair/atoms'
import {
  fetchUserPositionsRequestAction,
  positionsRegistry,
  reatomAggregatedPosition,
} from '@root/entities/position'
import { fetchProfilePositionsRequestAction } from '@root/entities/position/atoms/profile-position-requests'
import { walletIdsAtom } from '@root/entities/wallet'
import { reatomDataTable } from '@root/shared/ui-kit'

import { createProfilePositionsTableColumns } from '../ui/profile-positions-table-columns'

import type { PositionTableColumnId } from '../../positions-table/atoms'
import type { UserPositionsByDelugeIdCreatePayload } from '@blade/trade-api-client'
import type { AtomState } from '@reatom/framework'
import type {
  AggregatedPositionModel,
  WalletPositionModel,
} from '@root/entities/position'
import type { DelugeId } from '@root/entities/user-short'

export const reatomProfilePositionsTable = ({
  delugeId,
}: {
  delugeId: DelugeId
}) => {
  const paramsAtom = atom((ctx) => {
    const currentUserId = ctx.spy(delugeIdAtom)
    if (currentUserId !== delugeId) {
      return {
        userWalletIds: undefined,
      }
    }
    const userWalletIds = ctx.spy(walletIdsAtom)
    return {
      userWalletIds: [...userWalletIds],
    }
  }, 'paramsAtom')

  const tableModel = reatomDataTable<
    AggregatedPositionModel | WalletPositionModel,
    PositionTableColumnId,
    AtomState<typeof paramsAtom>,
    {
      onOpenWalletPositions?: (value: AggregatedPositionModel) => void
    }
  >({
    columns: createProfilePositionsTableColumns(),
    name: 'profile-positions-table',
    paramsAtom,
    loadItems: async (
      ctx,
      { sorting, pagination: { limit, offset }, params: { userWalletIds } },
    ) => {
      const network = ctx.get(selectedNetworkAtom)
      const sortBy = match(sorting?.sortColumn)
        .returnType<UserPositionsByDelugeIdCreatePayload['sortBy']>()
        .with('bought', () => 'boughtSize')
        .with('sold', () => 'soldSize')
        .with('remaining', () => 'remainingSizeUsd')
        .with('pnl', () => 'pnl')
        .with('lastTrade', () => 'lastTrade')
        .otherwise(() => 'lastTrade')

      const result = Result.unify(
        await ctx.schedule(() => {
          if (userWalletIds) {
            if (userWalletIds.length === 0) {
              return Result.succeed({ data: [] })
            }

            return fetchUserPositionsRequestAction(ctx, {
              network,
              limit,
              offset,
              sortBy,
              sortOrder: sorting?.sortDirection,
              walletIds: [...userWalletIds],
              hidden: false,
            })
          }

          return fetchProfilePositionsRequestAction(ctx, {
            delugeId,
            limit,
            offset,
            network,
            sortBy,
            sortOrder: sorting?.sortDirection,
            hidden: false,
          })
        }),
      )

      return result.pipe(
        Result.map((it) => {
          if (userWalletIds) {
            return {
              data: it.data.map((it) =>
                positionsRegistry.resolveFromDto(ctx, it),
              ),
            }
          }

          return {
            data: it.data.map((it) =>
              reatomAggregatedPosition({
                dto: it,
                pair: pairRegistry.resolveFromDto(ctx, it.pair),
              }),
            ),
          }
        }),
      )
    },
    limit: 20,
    initialSorting: () => {
      return {
        sortColumn: 'lastTrade',
        sortDirection: 'desc',
      }
    },
    getId: (position) =>
      'id' in position ? position.id : position.pairAddress,
  })

  return tableModel
}

export type ProfilePositionsTableModel = ReturnType<
  typeof reatomProfilePositionsTable
>
